import { useCallback, useState } from 'react'
import { Box } from '@mui/material'
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined'

import { useAnyCompassEmbed } from '@/features/anycompass'
import {
  appendMasterLines,
  useSoapMasters,
  type MasterInsertLine,
} from '@/features/soap-masters'
import { MasterInsertDialog } from '@/apps/latest/components/organisms/SoapMasters/MasterInsertDialog'
import { useSoapWrite } from '@/apps/latest/features/yakureki/hooks/useSoapWrite'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { DURATION } from '@/shared/styles/motion'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_WEIGHT,
  ICON_SIZE,
  scaledFontSize,
} from '@/shared/styles/typography'
import { useToast } from '@/shared/contexts/ToastContext'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'

interface Props {
  event: YakurekiDetail
  reloadEvent: () => Promise<void>
}

// 定型文の挿入導線 (Zendesk #2237 ③)。**ここは「使う」面で、マスタは書き換えない**
// (整えるのは 設定 > 定型文)。
//
// 露出条件は 3 つとも満たすとき:
//   1. AnyCOMPASS 埋め込み中 (= 連携ビルドにしか出ない。β ではない)
//   2. マスタが 1 件以上ある (0 件で押しても空なので affordance ごと隠す)
//   3. SOAP blob が既にある (= 挿入先がある)
//
// UI は SOAP 本文の下に低 emphasis なボタン 1 つだけ (= affordance を増やさない)。
export const MasterInsertAction = ({ event, reloadEvent }: Props) => {
  const { isEmbedded } = useAnyCompassEmbed()
  const { soapSets, hasAny, isLoading } = useSoapMasters(isEmbedded)
  const [open, setOpen] = useState(false)
  const { showToast } = useToast()
  const { write } = useSoapWrite({ eventId: event.id })
  const [isInserting, setIsInserting] = useState(false)

  // 挿入は既存の SOAP 保存経路にそのまま載せる (editSource='manual' = 手編集扱い)。
  // 各 section の末尾に追記するだけで、既存行の順序・採否は触らない。
  const handleInsert = useCallback(
    async (lines: MasterInsertLine[], label: string): Promise<boolean> => {
      if (lines.length === 0) {
        showToast('挿入できる行がありません', 'error')
        return false
      }
      setIsInserting(true)
      try {
        // 挿入元も cache の最新から取る (props の閉包から組むと、往復中の別編集を落とす)。
        const ok = await write(
          (current) => appendMasterLines(current, lines),
          'manual'
        )
        if (!ok) {
          showToast('挿入に失敗しました', 'error')
          return false
        }
        await reloadEvent()
        showToast(`「${label}」を挿入しました`, 'success')
        return true
      } finally {
        setIsInserting(false)
      }
    },
    [write, reloadEvent, showToast]
  )

  // isLoading の間は出さない (0 件と未着地を見分けられないので、押して空を見せてしまう)。
  if (!isEmbedded || isLoading || !hasAny || !event.soaps) return null

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="定型文を挿入"
        onClick={() => setOpen(true)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 1.5,
          // 幾何は PatientInfoBar の pill 列 (PillSelect trigger / 録音せずに記載) に
          // 揃える. 同じ「低 emphasis の inline pill」なのに 28/6px と 32/999 が
          // 混在していた. height ではなく minHeight にするのは文字拡大追従のため.
          minHeight: 32,
          px: 1.25,
          border: 'none',
          borderRadius: `${RADIUS.full}px`,
          fontFamily: 'inherit',
          fontSize: scaledFontSize('label'),
          fontWeight: FONT_WEIGHT.medium,
          color: colorTokens.fg.muted,
          bgcolor: 'transparent',
          cursor: 'pointer',
          transition: `background-color ${DURATION.fast}ms, color ${DURATION.fast}ms`,
          '&:hover': {
            bgcolor: colorTokens.overlay.hover,
            color: colorTokens.fg.default,
          },
          '&:focus': { outline: 'none' },
          '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
        }}
      >
        <PostAddOutlinedIcon sx={{ fontSize: ICON_SIZE.md }} />
        定型文
      </Box>
      <MasterInsertDialog
        open={open}
        onClose={() => setOpen(false)}
        soapSets={soapSets}
        onInsert={handleInsert}
        isInserting={isInserting}
      />
    </>
  )
}
